import { useEffect, useRef, useState, useCallback } from "react";

const ACTIVITY_EVENTS: (keyof DocumentEventMap)[] = [
  "mousemove",
  "mousedown",
  "keydown",
  "scroll",
  "touchstart",
  "click",
];

interface IdleTimeoutOptions {
  /** Total idle time before auto-logout (ms). Default: 15 minutes. */
  timeoutMs?: number;
  /** How long before logout to show the warning (ms). Default: 2 minutes. */
  warningMs?: number;
  /** Called when the idle timeout elapses. Should clear auth state + redirect. */
  onLogout: () => void;
}

interface IdleTimeoutResult {
  /** True when the warning countdown is visible. */
  showWarning: boolean;
  /** Seconds remaining until auto-logout (only meaningful when showWarning=true). */
  secondsLeft: number;
  /** Call this to reset the idle timer (e.g. "Stay logged in" button). */
  extendSession: () => void;
}

/**
 * Tracks user inactivity and triggers a warning then auto-logout.
 *
 * Attach activity listeners to document; every interaction resets the clock.
 * At (timeoutMs - warningMs) with no activity, showWarning flips true and a
 * countdown starts. At timeoutMs, onLogout is called.
 */
export function useIdleTimeout({
  timeoutMs = 15 * 60 * 1000,
  warningMs = 2 * 60 * 1000,
  onLogout,
}: IdleTimeoutOptions): IdleTimeoutResult {
  const lastActivityRef = useRef<number>(Date.now());
  const [showWarning, setShowWarning] = useState(false);
  const [secondsLeft, setSecondsLeft] = useState(Math.round(warningMs / 1000));

  const extendSession = useCallback(() => {
    lastActivityRef.current = Date.now();
    setShowWarning(false);
    setSecondsLeft(Math.round(warningMs / 1000));
  }, [warningMs]);

  useEffect(() => {
    const onActivity = () => {
      // Only reset if we're not already in the warning countdown — once the
      // warning is up the user must explicitly click "Stay logged in".
      if (!showWarning) {
        lastActivityRef.current = Date.now();
      }
    };

    ACTIVITY_EVENTS.forEach((evt) =>
      document.addEventListener(evt, onActivity, { passive: true })
    );
    return () => {
      ACTIVITY_EVENTS.forEach((evt) =>
        document.removeEventListener(evt, onActivity)
      );
    };
  }, [showWarning]);

  useEffect(() => {
    const tick = setInterval(() => {
      const idle = Date.now() - lastActivityRef.current;
      const remaining = timeoutMs - idle;

      if (remaining <= 0) {
        clearInterval(tick);
        onLogout();
        return;
      }

      if (remaining <= warningMs) {
        setShowWarning(true);
        setSecondsLeft(Math.ceil(remaining / 1000));
      } else {
        setShowWarning(false);
      }
    }, 1000);

    return () => clearInterval(tick);
  }, [timeoutMs, warningMs, onLogout]);

  return { showWarning, secondsLeft, extendSession };
}
